// Console-only, bounded, non-persistent observation. No customer identifiers.
/* global globalThis */
const TRACE_PATTERN = /^[a-f0-9]{32}$/;
const traces = new WeakSet();
const reminderTraces = new WeakMap();
let closingTrace = null;
const NUMBERS = new Set(['attempt', 'status', 'pauseMs']);
const OUTCOMES = new Set(['ok', 'error', 'confirmed', 'rejected', 'uncertain', 'stale', 'closed', 'blocked', 'discarded', 'not_observed']);

function randomTraceId() {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

export function createSaveDiagnostic(flow = 'reminder', {
  clock = () => performance.now(), log = line => console.log(line), idFactory = randomTraceId,
} = {}) {
  try {
    const traceId = idFactory();
    if (!TRACE_PATTERN.test(traceId || '')) return null;
    const started = clock();
    let count = 0;
    const trace = Object.freeze({
      traceId,
      mark(event, fields = {}) {
        try {
          if (count >= 160 || !/^[a-z_]{1,80}$/.test(event)) return;
          count += 1;
          const safe = {};
          for (const [key, value] of Object.entries(fields)) {
            if (NUMBERS.has(key) && Number.isFinite(value) && value >= 0) safe[key] = value;
            if (key === 'outcome' && OUTCOMES.has(value)) safe[key] = value;
          }
          log(`[save-diagnostic] ${JSON.stringify({
            side:'browser', traceId, flow:flow === 'prosp' ? 'prosp' : 'reminder',
            event, elapsedMs:Math.round(Math.max(0, clock() - started) * 100) / 100, ...safe,
          })}`);
        } catch { /* Diagnostic failure must not escape. */ }
      },
    });
    traces.add(trace);
    trace.mark('save_start');
    return trace;
  } catch { return null; }
}

export function diagnosticMark(trace, event, fields) {
  try { if (traces.has(trace)) trace.mark(event, fields); } catch { /* Fail open. */ }
}

export function diagnosticHeaders(trace, attempt) {
  try {
    if (!traces.has(trace)) return {};
    return {
      'X-CRM-Save-Trace':trace.traceId,
      ...(Number.isInteger(attempt) && attempt >= 1 && attempt <= 5
        ? { 'X-CRM-Save-Attempt':String(attempt) } : {}),
    };
  } catch { return {}; }
}

export function associateReminderDiagnostic(reminder, trace) {
  try { if (traces.has(trace)) reminderTraces.set(reminder, trace); } catch { /* No payload mutations. */ }
  return reminder;
}

export function reminderDiagnostic(reminder) {
  try { return reminderTraces.get(reminder); } catch { return undefined; }
}

// This context exists only during the synchronous, existing close callback.
export function observeDiagnosticClose(trace, close) {
  const previous = closingTrace;
  try {
    closingTrace = traces.has(trace) ? trace : null;
    diagnosticMark(trace, 'exit_guard_close_start');
    const result = close();
    diagnosticMark(trace, 'exit_guard_close_end', { outcome:result === true ? 'closed' : 'blocked' });
    return result;
  } catch (error) {
    diagnosticMark(trace, 'exit_guard_close_end', { outcome:'error' });
    throw error;
  } finally { closingTrace = previous; }
}

export function closingDiagnostic() { return closingTrace; }

// A frame with visible layout is an observation, NOT a promise of physical
// screen paint. No React state, polling request or business callback is changed.
export function observeListFrame(trace, event, {
  frame = callback => requestAnimationFrame(callback),
  find = () => document.querySelector('[data-save-diagnostic-conversation]')
    ? null : document.querySelector('[data-save-diagnostic-list="visible"]'),
  visible = node => {
    const rect = node.getBoundingClientRect();
    const style = getComputedStyle(node);
    return node.isConnected && rect.width > 0 && rect.height > 0
      && style.display !== 'none' && style.visibility !== 'hidden';
  },
  maxFrames = 120,
} = {}) {
  try {
    if (!traces.has(trace)) return;
    let frames = 0;
    const check = () => {
      try {
        frames += 1;
        const node = find();
        if (node && visible(node) && (event !== 'list_ready' || node.dataset.saveDiagnosticReady === 'true')) {
          diagnosticMark(trace, event);
        } else if (frames < maxFrames) {
          frame(check);
        } else {
          diagnosticMark(trace, 'list_frame_timeout', { outcome:'not_observed' });
        }
      } catch { /* Observers never affect rendering. */ }
    };
    frame(check);
  } catch { /* requestAnimationFrame may not exist in tests/background tabs. */ }
}